import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardCheck, PlayCircle, CheckCircle2, XCircle, RefreshCw,
  TrendingUp, TrendingDown, AlertTriangle, Calendar,
} from 'lucide-react';
import { formatApiError, getStoredUser, hasPermission } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentCycleId = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const cycleLabel = (id) => {
  if (!id || !id.includes('-')) return id;
  const [y, m] = id.split('-');
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const statusColor = {
  pending: 'bg-slate-100 text-slate-700',
  counted: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function CycleCounts() {
  const user = getStoredUser();
  const canGenerate = hasPermission(user, 'cycle_count.generate');
  const canCount = hasPermission(user, 'cycle_count.perform');
  const canApprove = hasPermission(user, 'cycle_count.approve');

  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState(currentCycleId());
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('all');
  const [inventoryType, setInventoryType] = useState('all');
  const [skuFilter, setSkuFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [countingItem, setCountingItem] = useState(null);
  const [countedInput, setCountedInput] = useState('');
  const [countedNotes, setCountedNotes] = useState('');

  const loadCycles = async () => {
    try {
      const r = await axios.get(`${API}/cycle-counts/cycles`);
      setCycles(r.data);
      if (r.data.length && !r.data.find((c) => c.cycle_id === activeCycle)) {
        setActiveCycle(r.data[0].cycle_id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadItems = async () => {
    if (!activeCycle) return;
    setLoading(true);
    try {
      const params = { cycle_id: activeCycle, limit: 500 };
      if (status !== 'all') params.status = status;
      if (inventoryType !== 'all') params.inventory_type = inventoryType;
      if (skuFilter) params.sku = skuFilter;
      const [i, r] = await Promise.all([
        axios.get(`${API}/cycle-counts`, { params }),
        axios.get(`${API}/cycle-counts/variance-report`, { params: { cycle_id: activeCycle } }),
      ]);
      setItems(i.data);
      setReport(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCycles(); }, []);
  useEffect(() => { loadItems(); }, [activeCycle, status, inventoryType]);

  const generate = async (payload) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/cycle-counts/generate`, payload);
      toast.success(`${res.data.created} item(s) added to cycle ${cycleLabel(res.data.cycle_id)}`);
      setActiveCycle(res.data.cycle_id);
      setGenOpen(false);
      await loadCycles();
      await loadItems();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const openCountDialog = (item) => {
    setCountingItem(item);
    setCountedInput(item.counted_qty ?? '');
    setCountedNotes(item.notes || '');
  };

  const submitCount = async () => {
    if (countedInput === '' || Number.isNaN(Number(countedInput)) || Number(countedInput) < 0) {
      toast.error('Enter a valid non-negative counted quantity');
      return;
    }
    try {
      await axios.patch(`${API}/cycle-counts/${countingItem.id}`, {
        counted_qty: Number(countedInput),
        notes: countedNotes || null,
      });
      toast.success('Count recorded');
      setCountingItem(null);
      loadItems();
      loadCycles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const approve = async (item) => {
    if (!window.confirm(
      `Approve count for ${item.sku}? ${item.variance !== 0
        ? `This will create a correction ledger entry of ${item.variance > 0 ? '+' : ''}${item.variance} unit(s).`
        : 'No variance — product qty will not change.'}`,
    )) return;
    try {
      await axios.post(`${API}/cycle-counts/${item.id}/approve`, {});
      toast.success('Approved');
      loadItems();
      loadCycles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (item) => {
    const notes = window.prompt('Rejection reason (optional):', '');
    if (notes === null) return;
    try {
      await axios.post(`${API}/cycle-counts/${item.id}/reject`, { notes });
      toast.success('Rejected — sent back to pending');
      loadItems();
      loadCycles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentCycleStats = useMemo(
    () => cycles.find((c) => c.cycle_id === activeCycle),
    [cycles, activeCycle],
  );

  return (
    <div className="space-y-6" data-testid="cycle-counts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="h-8 w-8 text-[#1680e5]" />
            Monthly Cycle Count
          </h1>
          <p className="text-slate-600 mt-2">
            Run a monthly physical count against system quantities. Approved variances auto-post as
            correction entries to the ledger.
          </p>
        </div>
        {canGenerate && (
          <Button
            onClick={() => setGenOpen(true)}
            className="bg-[#1680e5] hover:bg-[#1680e5]/90"
            data-testid="generate-cycle-button"
          >
            <PlayCircle className="h-4 w-4 mr-2" />Generate cycle
          </Button>
        )}
      </div>

      {/* Variance summary */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Calendar className="h-5 w-5 text-slate-500" />}
            label={cycleLabel(report.cycle_id)}
            value={report.total_items}
            sub={`${report.pending} pending · ${report.counted} to review · ${report.approved} approved`}
          />
          <StatCard
            icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
            label="Items with variance"
            value={report.items_with_variance}
          />
          <StatCard
            icon={<TrendingUp className="h-5 w-5 text-green-600" />}
            label="Overages"
            value={`+${report.total_overage_units}`}
            sub="units"
          />
          <StatCard
            icon={<TrendingDown className="h-5 w-5 text-red-600" />}
            label="Shortages"
            value={report.total_shortage_units}
            sub={`Est. value: ${report.estimated_variance_value >= 0 ? '+' : ''}${report.estimated_variance_value}`}
          />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>
            {currentCycleStats
              ? `${currentCycleStats.total} items in ${cycleLabel(activeCycle)}`
              : 'No cycle selected'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4 items-end">
            <div className="space-y-2">
              <Label>Cycle</Label>
              <Select value={activeCycle} onValueChange={setActiveCycle}>
                <SelectTrigger data-testid="cycle-selector"><SelectValue placeholder="Choose cycle" /></SelectTrigger>
                <SelectContent>
                  {cycles.length === 0 && <SelectItem value={currentCycleId()}>{cycleLabel(currentCycleId())}</SelectItem>}
                  {cycles.map((c) => (
                    <SelectItem key={c.cycle_id} value={c.cycle_id}>{cycleLabel(c.cycle_id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Status</Label>
              <Tabs value={status} onValueChange={setStatus}>
                <TabsList>
                  <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                  <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
                  <TabsTrigger value="counted" data-testid="tab-counted">To review</TabsTrigger>
                  <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-2">
              <Label>Inventory</Label>
              <Select value={inventoryType} onValueChange={setInventoryType}>
                <SelectTrigger data-testid="inventory-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>SKU search</Label>
              <div className="flex gap-2">
                <Input
                  value={skuFilter}
                  onChange={(e) => setSkuFilter(e.target.value)}
                  placeholder="SKU"
                  data-testid="sku-filter"
                  onKeyDown={(e) => e.key === 'Enter' && loadItems()}
                />
                <Button variant="outline" size="icon" onClick={loadItems}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No items in this cycle yet.
              {canGenerate && ' Click "Generate cycle" to snapshot current inventory.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Rack</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">System</TableHead>
                    <TableHead className="text-right">Counted</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`cc-row-${it.sku}`}>
                      <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                      <TableCell className="font-medium">{it.product_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{it.rack_location || '—'}</TableCell>
                      <TableCell>
                        <Badge className={it.inventory_type === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-teal-100 text-teal-800'}>
                          {it.inventory_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{it.system_qty}</TableCell>
                      <TableCell className="text-right">
                        {it.counted_qty ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {it.variance == null ? '—' : (
                          <span className={it.variance > 0 ? 'text-green-700 font-semibold' : it.variance < 0 ? 'text-red-700 font-semibold' : 'text-slate-500'}>
                            {it.variance > 0 ? '+' : ''}{it.variance}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColor[it.status]}>{it.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {it.status === 'pending' && canCount && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openCountDialog(it)}
                            data-testid={`count-btn-${it.sku}`}
                          >
                            Count
                          </Button>
                        )}
                        {it.status === 'counted' && canCount && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openCountDialog(it)}
                            data-testid={`recount-btn-${it.sku}`}
                          >
                            Re-count
                          </Button>
                        )}
                        {it.status === 'counted' && canApprove && (
                          <>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => approve(it)}
                              className="text-green-600"
                              data-testid={`approve-btn-${it.sku}`}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => reject(it)}
                              className="text-red-600"
                              data-testid={`reject-btn-${it.sku}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate cycle count</DialogTitle>
            <DialogDescription>
              Creates a snapshot of every product&apos;s current quantity for the selected month.
              Re-running is safe — only new products are added.
            </DialogDescription>
          </DialogHeader>
          <GenerateForm
            defaultCycleId={currentCycleId()}
            onCancel={() => setGenOpen(false)}
            onSubmit={generate}
            loading={generating}
          />
        </DialogContent>
      </Dialog>

      {/* Count entry dialog */}
      <Dialog open={!!countingItem} onOpenChange={(o) => !o && setCountingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Count {countingItem?.sku}</DialogTitle>
            <DialogDescription>
              {countingItem?.product_name} · Rack {countingItem?.rack_location || '—'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>System qty</Label>
                <div className="text-2xl font-bold text-slate-700">{countingItem?.system_qty}</div>
              </div>
              <div className="flex-1">
                <Label htmlFor="cc-counted">Counted qty</Label>
                <Input
                  id="cc-counted"
                  type="number"
                  min="0"
                  value={countedInput}
                  onChange={(e) => setCountedInput(e.target.value)}
                  autoFocus
                  data-testid="counted-qty-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cc-notes">Notes (optional)</Label>
              <Input
                id="cc-notes"
                value={countedNotes}
                onChange={(e) => setCountedNotes(e.target.value)}
                placeholder="Explain any discrepancy"
                data-testid="counted-notes-input"
              />
            </div>
            {countedInput !== '' && !Number.isNaN(Number(countedInput)) && countingItem && (
              <div className="text-sm p-3 rounded bg-slate-50 border">
                Variance: <span className="font-semibold">
                  {Number(countedInput) - countingItem.system_qty > 0 ? '+' : ''}
                  {Number(countedInput) - countingItem.system_qty} unit(s)
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCountingItem(null)}>Cancel</Button>
            <Button onClick={submitCount} className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="submit-count-btn">
              Save count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-slate-500">{label}</div>
            <div className="text-3xl font-bold text-slate-900 mt-1">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
          </div>
          <div>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function GenerateForm({ defaultCycleId, onCancel, onSubmit, loading }) {
  const [cycleId, setCycleId] = useState(defaultCycleId);
  const [inventoryType, setInventoryType] = useState('both');

  const submit = () => {
    const payload = { cycle_id: cycleId };
    if (inventoryType !== 'both') payload.inventory_type = inventoryType;
    onSubmit(payload);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="gen-cycle">Cycle month (YYYY-MM)</Label>
        <Input
          id="gen-cycle"
          value={cycleId}
          onChange={(e) => setCycleId(e.target.value)}
          placeholder="2026-08"
          data-testid="generate-cycle-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Include</Label>
        <Select value={inventoryType} onValueChange={setInventoryType}>
          <SelectTrigger data-testid="generate-inventory-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="both">All inventory</SelectItem>
            <SelectItem value="customer">Customer only</SelectItem>
            <SelectItem value="internal">Internal only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={submit}
          disabled={loading || !/^\d{4}-\d{2}$/.test(cycleId)}
          className="bg-[#1680e5] hover:bg-[#1680e5]/90"
          data-testid="generate-submit-btn"
        >
          {loading ? 'Generating…' : 'Generate'}
        </Button>
      </DialogFooter>
    </div>
  );
}
